import { useEffect, useState } from 'react'
import { login as loginMutation, getUserMutation } from 'api/mutations'
import { getCookie, removeCookieOnDomain } from 'utils/cookies'
import Url from 'utils/url'
import { criarUrlWithState } from 'components/modular/tabs-home/constants'
import { setCookieOnDomain } from 'utils/cookies'
import { useMutation } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import { getErrorMessage } from 'api/error'
import { useRecaptchaEnterprise } from 'utils/hooks/use-recaptcha-enterprise'

const useLogin = ({
  ROUTE_AFTER_LOGGED_IN,
  stateFromURL,
  clientId,
  isCriarRedirect
}) => {
  const [currentLogin, setCurrentLogin] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [currentStep, setCurrentStep] = useState('InitialForm')
  const [userName, setUserName] = useState('')
  const [passwordError, setPasswordError] = useState(null)
  const [loginError, setLoginError] = useState<string | null>(null)
  const [localInfosChecked, setLocalInfosIsChecked] = useState(false)

  const history = useHistory()
  const { executeRecaptcha } = useRecaptchaEnterprise()

  const handleLoginChange = event => {
    const userToken = event.target.value.trim()
    setLoginError(null)
    setCurrentLogin(userToken)
  }

  const handlePasswordChange = event => {
    setPasswordError(null)
    setPassword(event.target.value)
  }

  const handleNotMe = () => {
    setCurrentLogin('')
    setUserName('')
    setLoginError(null)
    setPasswordError(null)
    setPassword('')
    setCurrentStep('InitialForm')
  }

  const [loginWithPassword, { loading: loadingLoginWithPassword }] =
    useMutation(loginMutation)

  const handleLoginWithPassword = async () => {
    let dataLoginWithPassword = null
    setLoading(true)

    const recaptchaToken = await executeRecaptcha('LOGIN')

    await loginWithPassword({
      variables: {
        login: currentLogin,
        password: password,
        recaptchaToken: recaptchaToken
      },
      onCompleted: data => {
        dataLoginWithPassword = data
      },
      onError: error => {
        const errorMsg = getErrorMessage(error)
        switch (errorMsg) {
          case 'user found but password do not match':
            setPasswordError('wrongPassword')
            setLoading(loadingLoginWithPassword)
            break
          case 'reCAPTCHA verification failed':
            setPasswordError('recaptchaFailed')
            setLoading(loadingLoginWithPassword)
            break
          default:
            setPasswordError('general')
            setLoading(loadingLoginWithPassword)
        }
      }
    })

    setLoading(loadingLoginWithPassword)

    try {
      setCookieOnDomain('access_token', dataLoginWithPassword.login.token, 30)
      if (isCriarRedirect) {
        return Url.set(criarUrlWithState(clientId, stateFromURL))
      } else {
        history.push(ROUTE_AFTER_LOGGED_IN)
      }
      global.ga('send', {
        hitType: 'event',
        eventCategory: 'Metricas',
        eventAction: 'on login'
      })

      setLoading(loadingLoginWithPassword)
    } catch (err) {
      console.error('error: ', err)
      const customError = (err + '').match(
        /Error: GraphQL error: In field "login": CUSTOM_MESSAGE (.*)/
      )

      customError && global.alert(customError[1])
      setLoading(loadingLoginWithPassword)
    }
  }

  const [loginWithToken, { loading: loadingLoginWithToken }] =
    useMutation(getUserMutation)

  const handleInitialFormSubmit = async () => {
    let dataLoginWithToken = null
    setLoading(true)

    const recaptchaToken = await executeRecaptcha('LOGIN')

    await loginWithToken({
      variables: {
        login: currentLogin,
        recaptchaToken: recaptchaToken
      },
      onCompleted: data => {
        dataLoginWithToken = data
      }
    })

    setLoading(loadingLoginWithToken)

    try {
      if (dataLoginWithToken.getUser.response_type === 'error') {
        setLoading(false)
        setLoginError(dataLoginWithToken.getUser.info || 'user_not_found')
      }
      if (
        dataLoginWithToken.error &&
        dataLoginWithToken.error.message &&
        dataLoginWithToken.error.message
          .toLowerCase()
          .indexOf('graphql error: desativated') > -1
      ) {
        return global.alert(
          'Por favor, entre em contato com o SAC pelo e-mail sac@arvoreeducacao.com.br ou mensagem via Whatsapp (21) 98373-7146.'
        )
      }

      if (
        dataLoginWithToken.getUser.response_type === 'user_found_with_token'
      ) {
        global.ga('send', {
          hitType: 'event',
          eventCategory: 'Metricas',
          eventAction: 'on login'
        })
        setCookieOnDomain('access_token', dataLoginWithToken.getUser.info, 30)
        if (isCriarRedirect) {
          return Url.set(criarUrlWithState(clientId, stateFromURL))
        } else {
          return history.push(ROUTE_AFTER_LOGGED_IN)
        }
      }

      if (
        dataLoginWithToken.getUser.response_type === 'user_found_need_password'
      ) {
        const info = JSON.parse(dataLoginWithToken.getUser.info)
        setLoading(false)
        setUserName(info.name)
        setCurrentStep('IdentifiedUser')
      }
    } catch (err) {
      if (
        err &&
        err.message &&
        err.message.toLowerCase().indexOf('graphql error: desativated') > -1
      ) {
        removeCookieOnDomain('access_token')
        global.alert(
          'Por favor, entre em contato com o SAC pelo e-mail sac@arvoreeducacao.com.br ou mensagem via Whatsapp (21) 98373-7146.'
        )
      }
      setLoading(false)
      setLoginError('general')
    }
  }

  useEffect(() => {
    const token = getCookie('access_token')

    if (!token) {
      setLocalInfosIsChecked(true)
    }
  }, [])

  return {
    localInfosChecked,
    currentStep,
    userName,
    currentLogin,
    password,
    loading,
    loginError,
    passwordError,
    handleLoginChange,
    handlePasswordChange,
    handleInitialFormSubmit,
    handleLoginWithPassword,
    handleNotMe
  }
}

export default useLogin
